// VistaCancion.js
// Posiciona acordes midiendo el prefijo de la línea de acordes run a run,
// usando el tamaño de fuente de cada run individual (el autor a veces mezcla
// tamaños en una misma línea de acordes para ajustar el espaciado).

import React, { useState, useEffect } from "react";

const FUENTE = "'Comic Sans MS', 'Comic Sans', cursive";

const MARGEN_MIN = 4; // px mínimos entre acordes si aun así se solapan

function preservarEspacios(texto) {
  return texto.replace(/ {2,}/g, m => '\u00a0'.repeat(m.length));
}

let _canvas = null;
function getCtx() {
  if (!_canvas) _canvas = document.createElement("canvas");
  return _canvas.getContext("2d");
}

function medirPx(texto, tamPt) {
  const ctx = getCtx();
  ctx.font = `${tamPt}pt ${FUENTE}`;
  return ctx.measureText(texto).width;
}

/**
 * Dado runs_ac (lista de {t, tam}) y una posición en caracteres,
 * calcula cuántos px ocupa el prefijo hasta esa posición,
 * midiendo cada run con su tamaño propio.
 */
function medirPrefijoRunArun(runsAc, pos) {
  let px = 0;
  let consumido = 0;
  for (const run of runsAc) {
    if (consumido >= pos) break;
    const restante = pos - consumido;
    const fragmento = run.t.slice(0, restante);
    px += medirPx(fragmento, run.tam);
    consumido += run.t.length;
  }
  return px;
}

function calcularPosiciones(linea) {
  const { acordes, runs_ac, tam_acorde } = linea;
  if (!acordes || acordes.length === 0) return [];

  const tamA = tam_acorde || 12;
  const runsAc = runs_ac || [];

  const resultado = [];
  let derechaUltimo = -Infinity;

  for (const ac of acordes) {
    const leftNatural = runsAc.length > 0
      ? medirPrefijoRunArun(runsAc, ac.pos)
      : medirPx(' '.repeat(ac.pos), tamA);

    const leftFinal = Math.max(leftNatural, derechaUltimo + MARGEN_MIN);
    const anchoAcorde = medirPx(ac.texto, tamA);
    derechaUltimo = leftFinal + anchoAcorde;

    resultado.push({ texto: ac.texto, left: leftFinal, ancho: anchoAcorde });
  }

  return resultado;
}

function LineaConAcordes({ linea }) {
  const { texto, tam_letra, tam_acorde } = linea;
  const posiciones = calcularPosiciones(linea);

  const anchoContenedor = posiciones.length > 0
    ? posiciones[posiciones.length - 1].left + posiciones[posiciones.length - 1].ancho + 4
    : undefined;

  return (
    <div className="linea-bloque">
      <div
        className="linea-acorde-container"
        style={{
          fontSize: `${tam_acorde || 12}pt`,
          minWidth: anchoContenedor ? `${anchoContenedor}px` : undefined,
        }}
      >
        {posiciones.map((ac, i) => (
          <span key={i} className="acorde-inline" style={{ left: `${ac.left}px` }}>
            {ac.texto}
          </span>
        ))}
      </div>
      <div className="linea-letra" style={{ fontSize: `${tam_letra || 14}pt` }}>
        {preservarEspacios(texto || "") || "\u00a0"}
      </div>
    </div>
  );
}

function LineaSimple({ linea }) {
  return (
    <div className="linea-bloque">
      <div className="linea-letra" style={{ fontSize: `${linea.tam_letra || 14}pt` }}>
        {preservarEspacios(linea.texto || "") || "\u00a0"}
      </div>
    </div>
  );
}

function renderLineas(lineas, modoAcordes) {
  if (!lineas) return null;
  return lineas.map((linea, i) => {
    if (linea.acordes === null) return <div key={i} className="separador-estrofa" />;
    if (modoAcordes && linea.acordes && linea.acordes.length > 0)
      return <LineaConAcordes key={i} linea={linea} />;
    return <LineaSimple key={i} linea={linea} />;
  });
}

export function VistaCancion({ cancion, modoAcordes, onCategoriaClick }) {
  const [varianteActiva, setVarianteActiva] = useState(null);
  const [, setFuenteLista] = useState(false);

  useEffect(() => {
    document.fonts.ready.then(() => setFuenteLista(true));
  }, []);

  if (!cancion) return null;

  const lineasActivas =
    varianteActiva !== null && cancion.variantes?.[varianteActiva]
      ? cancion.variantes[varianteActiva].lineas
      : cancion.lineas;

  if (cancion.imagenUrl) {
    return (
      <div className="vista-cancion">
        <CabeceraCancion cancion={cancion} varianteActiva={varianteActiva}
          setVarianteActiva={setVarianteActiva} modoAcordes={modoAcordes}
          onCategoriaClick={onCategoriaClick} />
        <div className="cancion-imagen-container">
          <img src={cancion.imagenUrl} alt={`Partitura de ${cancion.titulo}`} className="cancion-imagen" />
        </div>
      </div>
    );
  }

  return (
    <div className="vista-cancion">
      <CabeceraCancion cancion={cancion} varianteActiva={varianteActiva}
        setVarianteActiva={setVarianteActiva} modoAcordes={modoAcordes}
        onCategoriaClick={onCategoriaClick} />
      <div className="cancion-contenido">
        {renderLineas(lineasActivas, modoAcordes)}
      </div>
    </div>
  );
}

function CabeceraCancion({ cancion, varianteActiva, setVarianteActiva, modoAcordes, onCategoriaClick }) {
  const tieneVariantes = cancion.variantes && cancion.variantes.length > 0;
  return (
    <div className="cancion-header">
      <h2 className="cancion-titulo">
        {cancion.numero && <span className="cancion-numero">{cancion.numero}. </span>}
        {cancion.titulo}
      </h2>
      <div className="cancion-meta">
        {cancion.autor && <span className="cancion-autor">{cancion.autor}</span>}
        {cancion.categorias?.length > 0 && (
          <span className="cancion-categoria">
            {cancion.categorias.map((cat, i) => (
              <React.Fragment key={cat}>
                {i > 0 && <span className="cat-sep"> · </span>}
                <button
                  className="cat-link"
                  onClick={() => onCategoriaClick && onCategoriaClick(cat)}
                  title={`Ver canciones de "${cat}"`}
                >
                  {cat}
                </button>
              </React.Fragment>
            ))}
          </span>
        )}
      </div>
      {modoAcordes && tieneVariantes && (
        <div className="cancion-tonalidades">
          <span className="tonalidad-label">Tonalidad:</span>
          <button className={`tonalidad-btn ${varianteActiva === null ? "activa" : ""}`}
            onClick={() => setVarianteActiva(null)}>Original</button>
          {cancion.variantes.map((v, i) => (
            <button key={i} className={`tonalidad-btn ${varianteActiva === i ? "activa" : ""}`}
              onClick={() => setVarianteActiva(i)}>{v.tonalidad}</button>
          ))}
        </div>
      )}
      <div className="cancion-enlaces">
        {cancion.youtube && (
          <a href={cancion.youtube} target="_blank" rel="noopener noreferrer" className="cancion-link">
            🎧 YouTube
          </a>
        )}
        {cancion.tiene_piano && (
          <span className="cancion-link cancion-link-info">🎹 Piano</span>
        )}
      </div>
    </div>
  );
}
